import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { FontAwesomeIcon } from "./FontAwesome";
import { ValidationErrorModel } from "../../models/ValidationErrorModel";
import { translate } from "../../models/utilities/translationUtilities";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import useStyles from "./ValidationError.styles";
import useDimensionContext from "../../hooks/useDimensionContext";
import { QuestionModel } from "../../models/QuestionModel";
import useRecall from "../../hooks/useRecall";
import { QuestionType } from "../../models/enums/QuestionType";

interface Props {
    error: ValidationErrorModel;
    question: QuestionModel;
    questionnaire: QuestionnaireModel;
    language: string;
}

function replaceArgs(text: string, args: { [name: string]: string }) {
    if (text == null || args == null) return text;
    return text.replace(/{(.*?)}/g, (x, g) => args[g]);
}

function getCustomErrorMessage(props: Props) {
    const { error, question, language } = props;
    const customErrorKey = "CustomErrorMessage";
    if (error.key === customErrorKey && error.message) {
        return error.message;
    }
    return (
        translate(question, customErrorKey, language) ||
        getSingleColumnCustomErrorMessage(question, customErrorKey, language)
    );
}

function getSingleColumnCustomErrorMessage(
    question: QuestionModel,
    customErrorKey: string,
    language: string,
) {
    const isSingleColumnStarRatingQuestion =
        question.type === QuestionType.StarRatingGrid && question.columns?.length === 1;

    if (isSingleColumnStarRatingQuestion) {
        const [column] = question.columns;
        return translate(column, customErrorKey, language);
    }

    return "";
}

function getMessageText(props: Props) {
    const { error, question, questionnaire, language } = props;
    if (!error) return "";

    let text = getCustomErrorMessage(props);
    if (!text) {
        const key = error.key || "InvalidAnswer";
        text =
            error.message ||
            translate(question, key, language) ||
            translate(questionnaire, key, language) ||
            key;
    }
    return replaceArgs(text, error.args);
}

export default function ValidationError(props: Props) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const dimension = useDimensionContext();
    const messageText = useRecall(getMessageText(props));

    const { question, error } = props;
    const isDisplayWide = dimension && dimension.minWidth(778);

    if (error === undefined) {
        return null;
    }

    return (
        <>
            <FontAwesomeIcon
                icon="exclamation-circle"
                className={classnames(classes.icon, isDisplayWide && classes.dynamicIcon)}
            />
            <div
                id={`${question.elementId}_ERROR`}
                className={classnames(
                    classes.validationError,
                    isDisplayWide && classes.dynamicValidationError,
                )}
                dangerouslySetInnerHTML={{ __html: messageText }}
            />
        </>
    );
}
